/**
 * Building blocks shared by the Overview widgets (screenshots/67, 69, 70, 71): the big-number stat (S layouts),
 * list rows, status chips, the projection bar (Likely yes · Toss-up · Likely no), the council-tension gauge and the
 * "All 11 toss-ups →" link. Sizes and colors follow the reference HTML (12px titles, 11px subs, 10px chips).
 */
import type { ReactNode } from "react";
import { Face } from "../../components/shared/Face";
import { navigate } from "../../app/router";
import type { ChipData, ProjSplit } from "../types";
import { OvIcon } from "../icons";
import { ListRow, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../../components/shared/List";

/** Click-through inside widgets keeps the SPA route (74 #7). */
export const go = (href?: string) => (e: React.MouseEvent) => {
  if (!href || e.metaKey || e.ctrlKey || !href.startsWith("/dashboard")) return;
  e.preventDefault();
  navigate(href);
};

/** S layout: one big value, an optional chip, one line under it (Next vote S, metrics, Toss-ups S…). */
export const Stat = ({ value, chip, sub, chipKind }: { value: ReactNode; chip?: ChipData; sub?: ReactNode; chipKind?: "count" }) => (
  <div className="ov-stat">
    <div className="ov-stat__value">
      <span className="ov-stat__v">{value}</span>
      {chip && <OvChip chip={chip} kind={chipKind} />}
    </div>
    {sub && <p className="ov-stat__sub">{sub}</p>}
  </div>
);

export const OvChip = ({ chip, icon, kind }: { chip: ChipData; icon?: string; kind?: "count" }) => (
  <span className={`ov-chip ov-chip--${chip.tone}${kind ? ` ov-chip--${kind}` : ""}`}>{icon && <OvIcon name={icon} size={12} />}{chip.text}</span>
);

/** One list row: optional lead (face / icon / date), title + sub, and a right-hand element. */
export function Row({ lead, title, sub, right, href, className }: {
  lead?: ReactNode; title: ReactNode; sub?: ReactNode; right?: ReactNode; href?: string; className?: string;
}) {
  // Hrefs outside the dashboard (none today) load normally, as before; dashboard ones keep the SPA route (List).
  const external = !!href && !href.startsWith("/dashboard");
  return (
    <ListRow as="div" className={`ov-row${className ? ` ${className}` : ""}`} href={href} wrap={false}
      onOpen={external ? () => window.location.assign(href!) : undefined}>
      {lead != null && lead !== false && <RowLead>{lead}</RowLead>}
      <RowMain min={0} className="ov-row__text">
        <RowTitle className="ov-row__title">{title}</RowTitle>
        {sub && <RowSub className="ov-row__sub">{sub}</RowSub>}
      </RowMain>
      {right != null && right !== false && <RowTrail>{right}</RowTrail>}
    </ListRow>
  );
}

export const RowFace = ({ name, photoUrl, size = 30 }: { name: string; photoUrl?: string | null; size?: number }) =>
  <span className="ov-row__face"><Face name={name} photoUrl={photoUrl} size={size} /></span>;

/** Likely yes · Toss-up · Likely no as one bar (forecast rows, whip count). Counts are roll-call projections. */
export function ProjBar({ proj, width = 120, height = 8 }: { proj: ProjSplit; width?: number | string; height?: number }) {
  const total = proj.yes + proj.tossup + proj.no || 1;
  return (
    <span className="ov-proj" style={{ width, height }} role="img" aria-label={`Likely yes ${proj.yes}, toss-up ${proj.tossup}, likely no ${proj.no}`}>
      <span className="ov-proj__y" style={{ flexGrow: proj.yes / total }} />
      <span className="ov-proj__t" style={{ flexGrow: proj.tossup / total }} />
      <span className="ov-proj__n" style={{ flexGrow: proj.no / total }} />
    </span>
  );
}

/** Council tension (69): 5 segments, label "Medium · 0.49". */
export function Tension({ t }: { t: number }) {
  const on = Math.max(1, Math.ceil(t * 5 - 1e-9));
  const label = t >= 0.5 ? "High" : t >= 0.25 ? "Medium" : "Low";
  return (
    <span className="ov-tension" role="img" aria-label={`${label} council tension, ${t.toFixed(2)}`}>
      <span className="ov-tension__segs" aria-hidden>{[0, 1, 2, 3, 4].map((i) => <span key={i} className={i < on ? "is-on" : undefined} />)}</span>
      <span className="ov-tension__label" aria-hidden>{label} · {t.toFixed(2)}</span>
    </span>
  );
}

/** "All 11 toss-ups →" / "Open target list →". */
export const More = ({ href, children }: { href: string; children: ReactNode }) => (
  <a className="ov-more" href={href} onClick={go(href)}>{children}<OvIcon name="arrow-right" /></a>
);

/** "+N more" when rows don't fit (§2.11 Live data: a widget's height never changes because of new data). */
export const Overflow = ({ n }: { n: number }) => (n > 0 ? <span className="ov-overflow">+{n} more</span> : null);

/** How many rows fit a widget body: rows are ~56px at L/M; reserve space for a footer link. */
export const fit = (h: number, rowPx: number, reserve = 0) => Math.max(1, Math.floor((h * 72 + (h - 1) * 16 - 54 - reserve) / rowPx));
